import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, TrendingUp, Wrench, Clock, Droplets, Snowflake, Fan, WashingMachine,
  AlertTriangle, UserPlus, ArrowRight, CheckCircle2,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import AdminLayout from "../AdminLayout";
import { adminAxios } from "../api";

const CAT_COLORS = { ro: "#0EA5E9", ac: "#1A3C6E", fan: "#7C3AED", washing_machine: "#0891B2" };
const SEG_COLORS = { home: "#FF6B2B", shop: "#22C55E", professional: "#F59E0B" };

const KPICard = ({ label, value, prefix, icon: Icon, accent, testid }) => (
  <div className="bg-white rounded-xl p-5 border border-slate-200" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="mt-2 font-display font-extrabold text-3xl text-slate-900">
          {prefix}{typeof value === "number" ? value.toLocaleString("en-IN") : value}
        </div>
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const MiniKPI = ({ label, value, icon: Icon, color, bg, testid }) => (
  <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-center gap-3" data-testid={testid}>
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: bg, color }}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <div className="text-xs text-slate-500 font-medium">{label}</div>
      <div className="font-display font-bold text-xl text-slate-900">{value}</div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [range, setRange] = useState(7);
  const [bookings, setBookings] = useState([]);

  const load = async () => {
    const [s, c, b] = await Promise.all([
      adminAxios.get("/dashboard/stats"),
      adminAxios.get(`/dashboard/charts?range=${range}`),
      adminAxios.get("/bookings?limit=10"),
    ]);
    setStats(s.data);
    setCharts(c.data);
    setBookings(b.data.items || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  // Auto-refresh live feed every 30s
  useEffect(() => {
    const t = setInterval(() => {
      adminAxios.get("/bookings?limit=10").then((r) => setBookings(r.data.items || [])).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, []);

  if (!stats || !charts) {
    return <AdminLayout title="Dashboard"><div className="text-slate-500">Loading…</div></AdminLayout>;
  }

  const k = stats.kpis;
  const sk = stats.service_kpis;
  const al = stats.alerts;

  return (
    <AdminLayout title="Dashboard">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="kpi-row">
        <KPICard testid="kpi-todays-bookings" label="Today's Bookings" value={k.todays_bookings} icon={Calendar} accent="bg-[#1A3C6E]/10 text-[#1A3C6E]" />
        <KPICard testid="kpi-completed-jobs" label="Completed Jobs" value={k.completed_jobs_today ?? 0} icon={CheckCircle2} accent="bg-[#22C55E]/10 text-[#22C55E]" />
        <KPICard testid="kpi-todays-revenue" label="Today's Revenue" prefix="₹" value={k.todays_revenue} icon={TrendingUp} accent="bg-[#16A34A]/10 text-[#16A34A]" />
        <KPICard testid="kpi-total-earned" label="Admin Earned" prefix="₹" value={k.total_earned ?? 0} icon={TrendingUp} accent="bg-[#0EA5E9]/10 text-[#0EA5E9]" />
        <KPICard testid="kpi-active-techs" label="Active Technicians" value={k.active_technicians} icon={Wrench} accent="bg-[#FF6B2B]/10 text-[#FF6B2B]" />
      </div>

      {/* Service-wise mini KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <MiniKPI testid="mini-ro" label="RO Today" value={sk.ro} icon={Droplets} bg="#E0F2FE" color="#0EA5E9" />
        <MiniKPI testid="mini-ac" label="AC Today" value={sk.ac} icon={Snowflake} bg="#DBEAFE" color="#1A3C6E" />
        <MiniKPI testid="mini-fan" label="Fan Today" value={sk.fan} icon={Fan} bg="#EDE9FE" color="#7C3AED" />
        <MiniKPI testid="mini-wm" label="Washing Mach. Today" value={sk.washing_machine} icon={WashingMachine} bg="#CFFAFE" color="#0891B2" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Trend (line) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2" data-testid="bookings-trend">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-slate-900">Bookings Trend</h3>
            <div className="inline-flex bg-slate-100 rounded-lg p-0.5 text-xs font-semibold">
              {[7, 30].map((r) => (
                <button
                  key={r}
                  data-testid={`trend-range-${r}`}
                  onClick={() => setRange(r)}
                  className={`px-3 py-1 rounded-md transition-all ${range === r ? "bg-white shadow text-[#1A3C6E]" : "text-slate-500"}`}
                >
                  {r}d
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts.trend}>
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Line type="monotone" dataKey="bookings" stroke="#1A3C6E" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue (bar) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="revenue-trend">
          <h3 className="font-display font-bold text-slate-900 mb-4">Revenue (₹)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.trend}>
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Bar dataKey="revenue" fill="#FF6B2B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pies + alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="category-pie">
          <h3 className="font-display font-bold text-slate-900 mb-4">By Category</h3>
          {charts.by_category.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-10">No data in range</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={charts.by_category} dataKey="value" nameKey="name" outerRadius={80} label>
                  {charts.by_category.map((c) => <Cell key={c.name} fill={CAT_COLORS[c.name] || "#94a3b8"} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          {charts.by_category_revenue && charts.by_category_revenue.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-2">Revenue by category</h4>
              <div className="space-y-2">
                {charts.by_category_revenue.map((c) => (
                  <div key={c.name} className="flex items-center justify-between text-sm">
                    <div className="capitalize text-slate-700">{c.name}</div>
                    <div className="font-semibold text-slate-900">₹{Number(c.value || 0).toLocaleString('en-IN')}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="segment-pie">
          <h3 className="font-display font-bold text-slate-900 mb-4">By Segment</h3>
          {charts.by_segment.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-10">No data in range</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={charts.by_segment} dataKey="value" nameKey="name" outerRadius={80} label>
                  {charts.by_segment.map((c) => <Cell key={c.name} fill={SEG_COLORS[c.name] || "#94a3b8"} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="alerts-panel">
          <h3 className="font-display font-bold text-slate-900 mb-4">Alerts</h3>
          <div className="space-y-2.5 text-sm">
            <AlertRow
              testid="alert-unassigned"
              icon={AlertTriangle} color="#EF4444" bg="#FEE2E2"
              label="Unassigned bookings"
              value={al.unassigned_bookings}
              to="/admin/bookings?status=confirmed"
            />
            <AlertRow
              testid="alert-pending-apps"
              icon={UserPlus} color="#F59E0B" bg="#FEF3C7"
              label="Pending technician approvals"
              value={al.pending_technician_approvals}
              to="/admin/applications?status=pending"
            />
            <AlertRow
              testid="alert-amc"
              icon={Calendar} color="#1A3C6E" bg="#DBEAFE"
              label="AMC renewals this week"
              value={al.amc_renewals_this_week}
              to="#"
            />
          </div>
        </div>
      </div>


      {/* Live bookings feed */}
      <div className="bg-white rounded-xl border border-slate-200 mt-6 overflow-hidden" data-testid="live-feed">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-slate-900">Live Bookings <span className="ml-2 text-xs font-medium text-slate-400">(auto-refresh 30s)</span></h3>
          <Link to="/admin/bookings" className="text-xs font-semibold text-[#1A3C6E] hover:text-[#FF6B2B] flex items-center gap-1">
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="px-5 py-2.5">ID</th>
                <th className="px-5 py-2.5">Customer</th>
                <th className="px-5 py-2.5">Service</th>
                <th className="px-5 py-2.5">Brand</th>
                <th className="px-5 py-2.5">City</th>
                <th className="px-5 py-2.5">Slot</th>
                <th className="px-5 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-400 text-sm">No bookings yet.</td></tr>
              )}
              {bookings.map((b) => (
                <tr key={b.booking_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`feed-row-${b.booking_id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/admin/bookings/${b.booking_id}`} className="font-semibold text-[#1A3C6E] hover:underline">
                      {b.booking_id}
                    </Link>
                  </td>
                  <td className="px-5 py-3">{b.name}<div className="text-xs text-slate-500">{b.phone}</div></td>
                  <td className="px-5 py-3">{b.service_name}</td>
                  <td className="px-5 py-3">{b.brand}</td>
                  <td className="px-5 py-3 capitalize">{b.city}</td>
                  <td className="px-5 py-3 capitalize">{b.slot}</td>
                  <td className="px-5 py-3"><StatusPill status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}

const AlertRow = ({ icon: Icon, color, bg, label, value, to, testid }) => (
  <Link to={to} data-testid={testid} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-colors">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: bg, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-medium text-slate-800">{label}</span>
    </div>
    <span className="font-display font-bold text-lg" style={{ color }}>{value}</span>
  </Link>
);

export const StatusPill = ({ status }) => {
  const map = {
    confirmed: { bg: "#DBEAFE", color: "#1A3C6E", label: "Confirmed" },
    assigned: { bg: "#FEF3C7", color: "#F59E0B", label: "Assigned" },
    on_the_way: { bg: "#EDE9FE", color: "#7C3AED", label: "On the way" },
    completed: { bg: "#DCFCE7", color: "#22C55E", label: "Completed" },
    cancelled: { bg: "#FEE2E2", color: "#EF4444", label: "Cancelled" },
  };
  const m = map[status] || { bg: "#F1F5F9", color: "#64748B", label: status };
  return (
    <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
};
